import React, { useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '../features/auth/stores/useAuthStore';

// We intentionally use raw axios here instead of lib/api.ts to avoid 
// an infinite loop of interceptors trying to add a non-existent guest ID 
// while we are trying to provision one.

// In-flight singleton promise to prevent duplicate concurrent provisioning calls
let provisioningPromise: Promise<string | null> | null = null;

export const ensureGuestSession = async (): Promise<string | null> => {
    const { isAuthenticated, guestId } = useAuthStore.getState();
    if (isAuthenticated) return null;
    if (guestId) return guestId;

    if (provisioningPromise) return provisioningPromise;

    provisioningPromise = (async () => {
        try {
            const res = await axios.post('/api/guest');
            if (res.data?.success && res.data?.data?.id) {
                const newId = res.data.data.id;
                useAuthStore.getState().setGuestId(newId);
                return newId;
            }
        } catch (error) {
            console.error("Failed to provision guest session via API, using fallback UUID:", error);
            // Fallback UUID if network was temporarily slow or offline
            const fallbackId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null;
            if (fallbackId) {
                useAuthStore.getState().setGuestId(fallbackId);
                return fallbackId;
            }
        } finally {
            provisioningPromise = null;
        }
        return null;
    })();

    return provisioningPromise;
};

export const GuestProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { guestId, isAuthenticated, clearGuestId } = useAuthStore();

    useEffect(() => {
        const provisionGuest = async () => {
            // Priority 1: User is logged in, no need for guest ID
            if (isAuthenticated) {
                if (guestId) clearGuestId();
                return;
            }

            // Priority 2: User has a guest ID from a previous session
            if (guestId) {
                try {
                    // Validate guest ID against the backend
                    await axios.get(`/api/guest/${guestId}`);
                    return; // Valid guest
                } catch (error: any) {
                    if (error.response?.status === 404) {
                        // Backend doesn't know this guest, clear and immediately reprovision
                        clearGuestId();
                        await ensureGuestSession();
                    }
                    return;
                }
            }

            // Priority 3: No guest ID, create one immediately
            await ensureGuestSession();
        };

        provisionGuest();
    }, [isAuthenticated, guestId, clearGuestId]);

    return <>{children}</>;
};
